import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import FiltersBar from '../components/FiltersBar';
import GalleryListing from '../components/GalleryListing';
import GalleryPagination from '../components/GalleryPagination';
import {ArtisticObjectListing} from '../../admin/artisticobjects/types/ArtisticObject';
import {handleError} from '../../common/utils/errorHandling';
import GalleryHeader from '../components/GalleryHeader';
import searchService from '../services/searchService';
import collectionsService, {CollectionDto} from '../services/collectionsService';
import {useSearch} from '../../common/contexts/SearchContext';
import preferencesService from '../../common/services/preferencesService';
import {getValueWithFallback} from '../../common/types/StringTranslations';

const CollectionView: React.FC = () => {
  const {t, i18n} = useTranslation();
  const {id} = useParams<{ id: string }>();
  const [collection, setCollection] = useState<CollectionDto | null>(null);
  const [artisticObjects, setArtisticObjects] = useState<ArtisticObjectListing[]>([]);
  const {searchCriteria, updateSearchCriteria, resetAndSetCollection} = useSearch();
  const [pageIndex, setPageIndex] = useState(0);
  const [isFilterInitialized, setIsFilterInitialized] = useState(false);

  const [zoomedId, setZoomedIn] = useState(() => preferencesService.getGalleryDisplay().zoomedIn);
  const [withDescriptions, setWithDescriptions] = useState(() => preferencesService.getGalleryDisplay().withDescriptions);
  const [columnsCount, setWithColumnsCount] = useState(() => preferencesService.getGalleryDisplay().columnsCount);
  const [itemsPerPage, setItemsPerPage] = useState(() => preferencesService.getGalleryPagination().itemsPerPage);

  useEffect(() => {
    if (id) {
      fetchCollection(id);
      resetAndSetCollection(id);
      setIsFilterInitialized(true);
    }
  }, [id]);

  useEffect(() => {
    if (isFilterInitialized) {
      console.log('searchCriteria changed', searchCriteria);
      searchArtisticObjects();
    } else {
      console.log("Ignoring searchCriteria change because filter is not initialized yet");
    }
  }, [searchCriteria, isFilterInitialized]);

  useEffect(() => {
    preferencesService.updateGalleryDisplay({zoomedIn: zoomedId});
  }, [zoomedId]);

  useEffect(() => {
    preferencesService.updateGalleryDisplay({withDescriptions});
  }, [withDescriptions]);

  useEffect(() => {
    preferencesService.updateGalleryDisplay({columnsCount});
  }, [columnsCount]);

  useEffect(() => {
    preferencesService.updateGalleryPagination({itemsPerPage});
  }, [itemsPerPage]);

  const fetchCollection = async (collectionId: string): Promise<void> => {
    await handleError(
      async () => {
        const data = await collectionsService.getCollection(collectionId);
        setCollection(data);
      },
      t('common.errors.fetchingCollection')
    );
  };

  const searchArtisticObjects = async (): Promise<void> => {
    await handleError(
      async () => {
        const objectsData = await searchService.searchArtisticObjects(searchCriteria);
        setArtisticObjects(objectsData);
        setPageIndex(0);
      },
      t('common.errors.searchingArtisticObjects')
    );
  };

  const handleItemsPerPageChange = (newItemsPerPage: number): void => {
    setItemsPerPage(newItemsPerPage);
    setPageIndex(0);
  };

  const totalCount = artisticObjects.length;
  const startIndex = pageIndex * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedObjects = artisticObjects.slice(startIndex, endIndex);

  const currentRangeStart = totalCount > 0 ? startIndex + 1 : 0;
  const currentRangeEnd = Math.min(endIndex, totalCount);

  if (!collection) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-gray-500">{t('collections.loading')}</div>
      </div>
    );
  }

  return (
    <>
      <FiltersBar
        searchCriteria={searchCriteria}
        onSearchCriteriaChange={updateSearchCriteria}
        disabledFilters={['collections', 'publications']}
      />
      <GalleryHeader
        title={getValueWithFallback(collection.title, i18n.language)}
        introduction={getValueWithFallback(collection.subtitle, i18n.language)}
        introductionHtml={getValueWithFallback(collection.description, i18n.language)}
        range={`${currentRangeStart}-${currentRangeEnd}`}
        totalCount={totalCount}
        zoomedIn={zoomedId}
        withDescriptions={withDescriptions}
        columnsCount={columnsCount}
        onToggleZoom={zoomedId => setZoomedIn(zoomedId)}
        onToggleDescriptions={withDescriptions => setWithDescriptions(withDescriptions)}
        onChangeColumns={columns => setWithColumnsCount(columns)}
      />
      <GalleryListing
        artisticObjects={displayedObjects}
        withDescriptions={withDescriptions}
        columnsCount={columnsCount}
        pagination={(
          <GalleryPagination
            pageIndex={pageIndex}
            totalCount={totalCount}
            itemsPerPage={itemsPerPage}
            onSetPageIndex={setPageIndex}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
        zoomedIn={zoomedId}
      />
    </>
  );
};

export default CollectionView;

